<template>
    <header class="relative z-10 flex-shrink-0 flex h-16 bg-white border-b border-gray-200 lg:border-none shadow-sm">
        <button
            type="button"
            class="px-4 border-r border-gray-200 text-gray-500 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-500 lg:hidden"
            @click="handleOpenMenu"
        >
            <span class="sr-only">Ouvrir le menu</span>
            <svg-vue icon="bars" class="h-6 w-6 fill-current"></svg-vue>
        </button>

        <div class="flex-1 flex justify-between px-4 sm:px-6">
            <div class="flex flex-1 items-center"></div>
            <div class="ml-4 flex items-center gap-2 md:ml-6">
                <div class="relative">
                    <button
                        type="button"
                        @click.stop="handleDropdownOpen('settings')"
                        class="rounded-full flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                        id="user-menu"
                        aria-haspopup="true"
                        :aria-expanded="is_dropdown_open === 'settings'"
                    >
                        <span class="sr-only">Menu utilisateur</span>
                        <span
                            class="h-8 w-8 rounded-full text-white text-xs font-semibold tracking-wide flex items-center justify-center cursor-pointer select-none"
                            :class="avatarClass"
                            :title="auth_user ? auth_user.name : ''"
                        >{{ initials }}</span>
                    </button>

                    <div
                        v-if="is_dropdown_open === 'settings'"
                        v-click-away="handleDropdownClose"
                        class="origin-top-right absolute top-full right-0 mt-2 w-56 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-20"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="user-menu"
                    >
                        <p class="px-4 py-2 text-sm text-gray-500 border-b border-gray-100">
                            Connecté en tant que
                            <span class="font-semibold text-gray-900">{{ auth_user.name }}</span>
                        </p>
                        <button
                            v-for="lang in langs"
                            :key="lang"
                            type="button"
                            class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                            :class="locale === lang ? 'font-semibold text-gray-900' : 'text-gray-700'"
                            role="menuitem"
                            :aria-pressed="locale === lang"
                            @click="handleSwitchLang(lang)"
                        >
                            {{ lang.toUpperCase() }}
                        </button>
                        <button
                            type="button"
                            class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                            role="menuitem"
                            @click="handleLogout"
                        >
                            Déconnexion
                        </button>
                    </div>
                </div>

                <div v-if="createItems.length" class="relative">
                    <button
                        type="button"
                        @click.stop="handleDropdownOpen('create')"
                        class="bg-custom-600 p-1 rounded-full flex items-center text-sm hover:bg-custom-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                        id="create-menu"
                        aria-haspopup="true"
                        :aria-expanded="is_dropdown_open === 'create'"
                    >
                        <span class="sr-only">Créer</span>
                        <svg-vue icon="plus" class="text-white fill-current h-6 w-6"></svg-vue>
                    </button>

                    <div
                        v-if="is_dropdown_open === 'create'"
                        v-click-away="handleDropdownClose"
                        class="origin-top-right absolute top-full right-0 mt-2 w-56 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-20"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="create-menu"
                    >
                        <button
                            v-for="item in createItems"
                            :key="item.label"
                            type="button"
                            class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            role="menuitem"
                            @click="item.action"
                        >
                            {{ item.label }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </header>
</template>

<script>
import { directive } from 'vue3-click-away';
import { userInitials } from '../../composables/userInitials';

export default {
    data() {
        return {
            is_dropdown_open: null,
        };
    },
    computed: {
        auth_user() {
            return this.$store.state.auth.auth_user;
        },
        locale() {
            return this.$store.state.config.locale;
        },
        langs() {
            return this.$store.state.config.langs;
        },
        initials() {
            if (!this.auth_user) {
                return '?';
            }

            return userInitials(this.auth_user.name, this.auth_user.email);
        },
        avatarClass() {
            const source = this.auth_user
                ? (this.auth_user.name || this.auth_user.email || '')
                : '';
            const colors = ['bg-teal-600', 'bg-cyan-600', 'bg-indigo-600', 'bg-sky-600', 'bg-emerald-600'];
            let hash = 0;

            for (let index = 0; index < source.length; index++) {
                hash = source.charCodeAt(index) + ((hash << 5) - hash);
            }

            return colors[Math.abs(hash) % colors.length];
        },
        isRoot() {
            return this.$store.getters['auth/isRoot'];
        },
        canImportSources() {
            return this.auth_user && this.auth_user.can_import_sources === true;
        },
        hasDomainContext() {
            return Boolean(this.$store.state.domains.single && this.$store.state.domains.single.id);
        },
        createItems() {
            const items = [];

            if (this.isRoot) {
                items.push({
                    label: 'Créer une corporation',
                    action: () => this.openCreate('corporations', 'corporation-save'),
                });
            }

            if (this.hasDomainContext) {
                items.push({
                    label: 'Créer un projet',
                    action: () => this.openCreate('projects', 'project-save'),
                });
            }

            items.push({
                label: 'Créer un utilisateur',
                action: () => this.openCreate('users', 'user-save'),
            });

            if (this.canImportSources) {
                items.push({
                    label: 'Importer des sources',
                    action: () => this.goTo('source-imports'),
                });
            }

            return items;
        },
    },
    methods: {
        handleDropdownOpen(name) {
            this.is_dropdown_open = this.is_dropdown_open === name ? null : name;
        },
        handleDropdownClose() {
            this.is_dropdown_open = null;
        },
        handleSwitchLang(lang) {
            if (this.locale === lang) {
                this.handleDropdownClose();

                return;
            }

            this.$store.dispatch('config/setLocale', lang);
            const current = this.$route;
            const params = Object.assign({}, current.params, { locale: lang });

            if (current.name) {
                this.$router.push({ name: current.name, params, query: current.query });
            } else {
                this.$router.push({ path: `/${lang}` });
            }

            this.handleDropdownClose();
        },
        handleLogout() {
            this.handleDropdownClose();

            return this.$emit('logout');
        },
        handleOpenMenu() {
            return this.$emit('openMenu');
        },
        openCreate(resource, modal) {
            this.$store.dispatch(`${resource}/resetSingle`);
            this.$store.dispatch('modals/open', modal);
            this.handleDropdownClose();
        },
        goTo(name) {
            this.handleDropdownClose();
            this.$router.push({ name });
        },
    },
    directives: {
        ClickAway: directive,
    },
};
</script>
